import { DEFAULT_MIRA_MODE, MiraMode, toMiraMode } from "@/data/models/MiraMode";
import { auth, firestore } from "firebase/app";
import Client, {
  ClientsInfoOfGroup,
  ClientType,
  createDefaultClientConfiguration,
  createDefaultDeviceSettingsDeprecated,
  createDefaultMemberClientsConfiguration,
  createrDefaultDailyTrackingSettings,
  createrDefaultTimeTrackingConfigurations,
  DailyTrackingSettings,
  DEFAULT_COMPANY_NAME,
  ErpIntegration,
  MemberClientDetails,
  MemberClientsConfiguration,
  ProjectManagement,
  RenoActivationScope,
  TimeTrackingConfigurations,
} from "@/data/models/Client";
import { ClientInfo } from "@/data/models/ClientInfo";
import "firebase/auth";
import { db, functions } from "@/main";
import { getString, setString } from "@/utils/SecureLocalStorage";
import { filterOutUndefined, generateRandomUuid } from "@/utils/Utils";
import { uploadImage } from "./FirebaseImageRepository";
import { Branch, MemberClientBranch } from "../models/Branch";
import { ComBoxHumidityUnit } from "../models/ComBoxHumidityUnit";
import { LocalSettingsModule } from "@/store/modules/LocalSettingsModule";
import {
  buildComBoxClientConfigInfo,
  ComBoxClientConfig,
} from "../models/ComBoxClientConfig";
import { getIresClientType, IresClientType } from "../models/IresClient";
import {
  createDefaultHyDryMessagesSettings,
} from "../models/HyDryMessagesConfig";
import { changeLanguageTo } from "@/i18n/Languages";
import { changeThemeTo } from "@/utils/Theme";
import { DEFAULT_LOCALE } from "@/plugins/vuetify";
import { UserModule } from "@/store/modules/UserModule";
import { SystemConfigurationModule } from "@/store/modules/SystemConfigurationModule";
import { ProjectSupervisorSource } from "../models/Settings";
import { UserSettingsModule } from "@/store/modules/UserSettingsModule";
import { CompanySettingsModule } from "@/store/modules/CompanySettingsModule";
import { getMemberClientsUserSettings } from "./SettingsRepository";
import { HyDryDeviceSettingsModule } from "@/store/modules/HyDryDeviceSettingsModule";
import { ClientModule } from "@/store/modules/ClientModule";
import { LoginResult, loginResultForRefusal } from "@/components/login/LoginTypes";
import { toSignInRefusalReason } from "@/utils/SignInRefusal";
import { storeActiveUser } from "./UserRepository";
import User, { Role } from "../models/User";
import { buildSwitchedSessionUser } from "@/utils/MemberClientSwitchUtils";

const KEY_CLIENT_INFO = "CLIENT_INFO";
const ADMINISTRATOR_SESSION_USER_KEY = "administratorSessionUser";

function getAdministratorSessionUser(): User | null {
  const userJson = localStorage.getItem(ADMINISTRATOR_SESSION_USER_KEY);

  if (!userJson) {
    return null;
  }

  try {
    return JSON.parse(userJson) as User;
  } catch (error) {
    return null;
  }
}

export enum ClientSettings {
  SYSTEM_CONFIGURATION = "systemConfiguration",
  ROLE_SETTINGS = "roleSettings",
  DEVICE_SETTINGS = "deviceSettings",
  REPORT_SETTINGS = "reportSettings"
}

export function getClientId() {
  return auth().currentUser?.uid ?? "";
}

export function getClientInfo() {
  const json = getString(KEY_CLIENT_INFO);
  
  if (!json) {
    return null;
  }
  
  const clientInfo = JSON.parse(json) as ClientInfo;
  
  if (!clientInfo) {
    return null;
  }
  
  return clientInfo;
}

export function getClientRefById(clientId: string) {
  return "clients/" + clientId;
}

export function getAdministratorClientId() {
  return getClientInfo()?.administratorClientId ?? undefined;
}

export function isMemberClient() {
  return getAdministratorClientId() !== undefined;
}

export function getClientsAdministrationRefByAdministratorId(administratorClientId: string) {
  return "clientsAdministration/" + administratorClientId;
}

export function getIgnoreIresAdminOrIresClientCheck() {
  return ClientModule.ignoreIresAdminOrIresClientCheck;
}

export function hasAdministrationCapabilities() {
  return getClientInfo()?.hasAdministrationCapabilities ?? false;
}

/**
 * Retrieves the administrator client ID based on the user's capabilities.
 *
 * @returns {Promise<string | null>} The client ID if the user has administration capabilities
 *                                   or the administrator's client ID, otherwise null.
 */
export function getClientsAdministratorId(): string | null {
  if (hasAdministrationCapabilities()) {
    return getClientId();
  }

  return getAdministratorClientId() || null;
}

export function getClientsAdministrationRef() {
  const administratorClientId = getClientsAdministratorId();

  if (!administratorClientId) {
    return "";
  }

  return "clientsAdministration/" + administratorClientId;
}

export function getClientRef() {
  const clientId = getClientId();
  
  if (!clientId) {
    throw Error("user not logged in");
  }
  
  return "clients/" + clientId;
}

export async function setLogoFile(file: File) {
  const parts = file.name.split(".");
  const extension = parts[parts.length - 1] ?? "png";
  const fileName = generateRandomUuid() + "." + extension;
  
  const setFileName = db.doc(getClientRef()).set(
    {
      logoFile: fileName,
    },
    { merge: true },
  );
  
  const imageUpload = uploadImage(fileName, file);
  
  await Promise.all([setFileName, imageUpload]);
  
  return fileName;
}

export async function getClientById(clientId: string) {
  return (
    await db.doc(getClientRefById(clientId)).get()
  ).data() as Client;
}

/**
 * Fetches multiple client documents from Firestore based on an array of client IDs and
 * returns them as a Map where each key is a clientId and each value is the corresponding Client object.
 * 
 * @param clientIds An array of client IDs for which to fetch the client documents.
 * @returns A Promise that resolves to a Map with clientId as key and Client object as value.
 * If a specific client document does not exist or an error occurs, that clientId will not be included in the returned Map.
 */
 export async function getClientsByIds(clientIds: string[]): Promise<Map<string, Client>> {
  // Initialize an empty map to store clientId-Client pairs
  const clientsMap = new Map<string, Client>();

  // Map each clientId to a promise that fetches the client document from Firestore
  const clientPromises = clientIds.map(async (clientId) => {
    const clientRef = getClientRefById(clientId); // Get the document reference for the clientId
    const snapshot = await db.doc(clientRef).get(); // Fetch the document snapshot from Firestore
    if (snapshot.exists) {
      const client = snapshot.data() as Client;

      // A deleted member is hidden like an inactive one (#2610067).
      if (client.isActive !== false && client.deleted !== true) {
        clientsMap.set(clientId, client); // Add the clientId-Client pair to the map if the document exists 
      }
    }
  });

  // Wait for all client fetch promises to resolve
  await Promise.all(clientPromises);

  // Return the map of clientId-Client pairs
  return clientsMap;
}

export async function getMemberClientDetailsByIds(memberClientIds: string[]): Promise<MemberClientDetails[]> {
  const memberClients: MemberClientDetails[] = [];

  const memberClientPromises = memberClientIds.map(async (memberClientId) => {
    const clientRef = getClientRefById(memberClientId);
    const snapshot = await db.doc(clientRef).get();
    if (snapshot.exists) {
      const memberClient = snapshot.data() as MemberClientDetails;
      // A deleted member is hidden like an inactive one (#2610067).
      const isMemberClientActive =
        (snapshot.data() as ClientInfo).isActive !== false && (snapshot.data() as Client).deleted !== true;

      if (memberClient && isMemberClientActive) {
        memberClients.push(memberClient);
      }
    }
  });

  await Promise.all(memberClientPromises);

  return memberClients;
}

export function getErpIntegration() {
  return getClientInfo()?.erpIntegration ?? ErpIntegration.NONE;
}

export function isSimulatingErp() {
  return getClientInfo()?.isTestSystem ?? false;
}

export function getLogoFile() {
  return getClientInfo()?.logoFile;
}

export function getMemberClientIdForSettings() {
  return getClientInfo()?.memberClientIdForSettings ?? undefined;
}

export function getMemberClientIds() {
  return getClientInfo()?.memberClientIds ?? [];
}

export function getClientConfiguration() {
  return getClientInfo()?.clientConfiguration ?? createDefaultClientConfiguration();
}

/**
 * Retrieves the comprehensive configuration for member clients, ensuring default values are applied where
 * specific configurations are not explicitly set. The function merges the stored configuration (if present)
 * with sensible defaults and **always filters all member‑list fields to only include currently active member clients**.
 *
 * The list of active member clients is provided by the caller via `activeMemberClientIds`. Internally, the
 * function also excludes the current client (administrator) from defaults. If any of the stored
 * `memberClientsWithOwnX...` arrays are missing, they default to the filtered list of active member client IDs.
 *
 * The resulting configuration allows (by default) all *active* member clients to define their own settings
 * across multiple domains unless overridden by the stored configuration.
 *
 * @param {string[]} activeMemberClientIds - The IDs of member clients that are currently active. All
 *   `memberClientsWithOwnX...` arrays in the returned object are filtered to this set.
 *
 * @returns {MemberClientsConfiguration} A fully resolved configuration object where:
 * - `defineOwnRoleSettings` (boolean) defaults to `true` when not set.
 * - `memberClientsWithOwnRoleSettings` defaults to all **active** member clients (excluding the current client).
 * - `defineOwnSystemConfiguration` (boolean) defaults to `true`.
 * - `memberClientsWithOwnSystemConfiguration` defaults to all **active** member clients.
 * - `defineOwnReportSettings` (boolean) defaults to `true`.
 * - `memberClientsWithOwnReportSettings` defaults to all **active** member clients.
 * - `defineOwnUserSettings` (boolean) defaults to `true`.
 * - `memberClientsWithOwnUserSettings` defaults to all **active** member clients.
 * - `defineOwnCompanySettings` (boolean) defaults to `true`.
 * - `memberClientsWithOwnCompanySettings` defaults to all **active** member clients.
 * - `defineOwnHyDryDeviceSettings` (boolean) defaults to `true`.
 * - `memberClientsWithOwnHyDryDeviceSettings` defaults to all **active** member clients.
 * - `defineOwnProjectManagementSettings` (boolean) defaults to `true`.
 * - `memberClientsWithOwnProjectManagementSettings` defaults to all **active** member clients.
 * - `defineOwnAllDevicesSettings` (boolean) defaults to `true`.
 * - `memberClientsWithOwnAllDevicesSettings` defaults to all **active** member clients.
 * - `defineOwnHyDryMessagesSettings` (boolean) defaults to `true`.
 * - `memberClientsWithOwnHyDryMessagesSettings` defaults to all **active** member clients.
 *
 * @example
 * // Suppose you resolved the active member client IDs elsewhere
 * const activeIds = ["c1", "c2", "c3"]; 
 * const config = getMemberClientsConfiguration(activeIds);
 * console.log(config.memberClientsWithOwnUserSettings); // filtered to only active IDs
 */
export function getMemberClientsConfiguration(activeMemberClientIds?: string[]) {
  const memberClientsConfiguration = getClientInfo()?.memberClientsConfiguration || createDefaultMemberClientsConfiguration();
  const activeSet = new Set(activeMemberClientIds ?? []);
  const memberClientIds = getMemberClientIds().filter(
    id => id !== getClientId() && (!activeMemberClientIds || activeSet.has(id))
  );
  const filterActive = (ids?: string[]) =>
    (ids ?? memberClientIds).filter(id => !activeMemberClientIds || activeSet.has(id));

  return {
    defineOwnRoleSettings: memberClientsConfiguration.defineOwnRoleSettings ?? true,
    memberClientsWithOwnRoleSettings: filterActive(memberClientsConfiguration.memberClientsWithOwnRoleSettings),

    defineOwnSystemConfiguration: memberClientsConfiguration.defineOwnSystemConfiguration ?? true,
    memberClientsWithOwnSystemConfiguration: filterActive(memberClientsConfiguration.memberClientsWithOwnSystemConfiguration),

    defineOwnReportSettings: memberClientsConfiguration.defineOwnReportSettings ?? true,
    memberClientsWithOwnReportSettings: filterActive(memberClientsConfiguration.memberClientsWithOwnReportSettings),

    defineOwnUserSettings: memberClientsConfiguration.defineOwnUserSettings ?? true,
    memberClientsWithOwnUserSettings: filterActive(memberClientsConfiguration.memberClientsWithOwnUserSettings),

    defineOwnCompanySettings: memberClientsConfiguration.defineOwnCompanySettings ?? true,
    memberClientsWithOwnCompanySettings: filterActive(memberClientsConfiguration.memberClientsWithOwnCompanySettings),

    defineOwnHyDryDeviceSettings: memberClientsConfiguration.defineOwnHyDryDeviceSettings ?? true,
    memberClientsWithOwnHyDryDeviceSettings: filterActive(memberClientsConfiguration.memberClientsWithOwnHyDryDeviceSettings),

    defineOwnProjectManagementSettings: memberClientsConfiguration.defineOwnProjectManagementSettings ?? true,
    memberClientsWithOwnProjectManagementSettings: filterActive(memberClientsConfiguration.memberClientsWithOwnProjectManagementSettings),

    defineOwnAllDevicesSettings: memberClientsConfiguration.defineOwnAllDevicesSettings ?? true,
    memberClientsWithOwnAllDevicesSettings: filterActive(memberClientsConfiguration.memberClientsWithOwnAllDevicesSettings),

    defineOwnHyDryMessagesSettings: memberClientsConfiguration.defineOwnHyDryMessagesSettings ?? true,
    memberClientsWithOwnHyDryMessagesSettings: filterActive(memberClientsConfiguration.memberClientsWithOwnHyDryMessagesSettings),
  } as MemberClientsConfiguration;
}

export function isOperationalClient() {
  return getClientInfo()?.isOperationalClient ?? true;
}

export function observeBranchesOfGroup(
  onNewBranchesOfGroup: (memberClientBranches: MemberClientBranch[]) => void
) {
  const memberClientBranches: MemberClientBranch[] = [];
  const clientsAdministrationRef = getClientsAdministrationRef();

  if (!clientsAdministrationRef || clientsAdministrationRef === "") {
    onNewBranchesOfGroup(memberClientBranches);
    return () => [];
  }

  return db.doc(clientsAdministrationRef).onSnapshot((snapshot) => {
    if (snapshot.exists) {
      const clientsAdministration = snapshot.data();

      if (clientsAdministration?.branches) {
        const keys = Object.keys(clientsAdministration.branches);
        
        for (const key of keys) {
          memberClientBranches.push(clientsAdministration.branches[key] as MemberClientBranch);
        }
      }
      onNewBranchesOfGroup(memberClientBranches.filter(branch => !branch.deleted));
    }
  });
}

export function observeMemberClientsInformationOfGroup(
  onNewClientsOfGroup: (memberClientsInfo: Partial<Client>[]) => void
) {
  const memberClientsInfo: Partial<Client>[] = [];
  const clientsAdministrationRef = getClientsAdministrationRef();

  if (!clientsAdministrationRef || clientsAdministrationRef === "") {
    onNewClientsOfGroup(memberClientsInfo);
    return () => [];
  }

  return db.doc(clientsAdministrationRef).onSnapshot((snapshot) => {
    if (snapshot.exists) {
      const clientsAdministration = snapshot.data();

      if (clientsAdministration?.clients) {
        const keys = Object.keys(clientsAdministration.clients);
        
        for (const key of keys) {
          memberClientsInfo.push(clientsAdministration.clients[key] as Partial<Client>);
        }
      }
      onNewClientsOfGroup(memberClientsInfo);
    }
  });
}

export async function getMemberClientsInformationOfGroup(
  clients?: string[]
): Promise<Map<string, ClientsInfoOfGroup>> {
  const memberClientsMap = new Map<string, ClientsInfoOfGroup>();
  const clientsAdministrationRef = getClientsAdministrationRef();

  if (!clientsAdministrationRef || clientsAdministrationRef === "") {
    return memberClientsMap;
  }

  try {
    const snapshot = await db.doc(clientsAdministrationRef).get();

    if (snapshot.exists) {
      const clientsAdministration = snapshot.data();

      if (clientsAdministration?.clients) {
        const keys = Object.keys(clientsAdministration.clients);
        
        for (const key of keys) {
          const client = clientsAdministration.clients[key] as ClientsInfoOfGroup;
          if (
            client && 
            (!clients || clients.includes(client.clientId)) &&
            client.isActive !== false &&
            // Mirrored by IRES Cloud since #2610067: a deleted member is hidden like an inactive one.
            client.deleted !== true
          ) {
            memberClientsMap.set(key, client);
          }
        }
      }
    }
  } catch (error) {
    return memberClientsMap;
  }

  return memberClientsMap;
}

export function getBranchesOfClient() {
  return (
    getClientInfo()?.branches?.filter(branch => !branch.deleted)
      .sort(
        (b1, b2) =>
          (b1.branchSortValue ?? b1.branchNumber) -
          (b2.branchSortValue ?? b2.branchNumber),
      ) ?? []
  );
}

export function getBranchesOfMemberClient(memberClient: Client) {
  const branches: Branch[] = [];

  if (memberClient.branches) {
    const keys = Object.keys(memberClient.branches);
    
    for (const key of keys) {
      branches.push(memberClient.branches[key] as Branch);
    }

    return branches
      .filter(branch => !branch.deleted)
      .sort(
        (b1, b2) =>
          (b1.branchSortValue ?? b1.branchNumber) -
          (b2.branchSortValue ?? b2.branchNumber),
      );
  }

  return branches;
}

export function getProjectManagementType() {
  return getClientInfo()?.projectManagement ?? ProjectManagement.DEFAULT;
}

export function isBelforClient() {
  return (
    getClientInfo()?.visibleTo?.includes("FoZdFzoHyxfxEspXMBR6gnh08QE3") ??
    false
  );
}

export function getProjectSupervisorSource() {
  // Attempt to retrieve the projectSupervisorSource from userSettings first.
  const supervisorSourceFromUserSettings =
    UserSettingsModule.userSettings?.projectSupervisorSource;

  // If not found, attempt to retrieve it from clientInfo.
  const supervisorSourceFromClientInfo = getClientInfo()
    ?.projectSupervisorSource;

  // Return the first defined source or default to SUPERVISOR_OF_PROJECT_OWNER if both are undefined.
  return (
    supervisorSourceFromUserSettings ??
    supervisorSourceFromClientInfo ??
    ProjectSupervisorSource.SUPERVISOR_OF_PROJECT_OWNER
  );
}

/**
 * Retrieves the strictBranchFilters configuration for a given member client.
 * 
 * @param {Client} memberClient - The client object containing user settings and client info.
 * @returns {Promise<boolean>} - A promise that resolves to the strictBranchFilters configuration.
 *                               It will first check the user settings, then the client info,
 *                               and if not found, defaults to false.
 */
export async function getMemberClientsStrictBranchFiltersConfiguration(memberClient: Client) {
  const userSettings = await getMemberClientsUserSettings(memberClient);

  // Attempt to retrieve the strictBranchFilters from userSettings first.
  const strictBranchFiltersFromUserSettings =
    userSettings?.strictBranchFilters;

  if (strictBranchFiltersFromUserSettings !== undefined) {
    return strictBranchFiltersFromUserSettings;
  }

  // If not found, attempt to retrieve it from client info.
  const strictBranchFiltersFromClientInfo = memberClient.userSettings?.strictBranchFilters;

  return (
    strictBranchFiltersFromClientInfo ?? false
  );
}

export function hasClientEnabledStrictBranchFilters() {
  // Attempt to retrieve the strictBranchFilters from userSettings first.
  const userSettings = UserSettingsModule.userSettings;
  const strictBranchFiltersFromUserSettings = userSettings?.strictBranchFilters;

  if (strictBranchFiltersFromUserSettings !== undefined && !userSettings.areDefaultUserSettings) {
    return strictBranchFiltersFromUserSettings;
  }

  // If not found, attempt to retrieve it from clientInfo.
  const strictBranchFiltersFromClientInfo = getClientInfo()
    ?.strictBranchFilters;

  return (
    strictBranchFiltersFromClientInfo ?? false
  );
}

/**
 * @deprecated getComBoxClientConfigInfo is deprecated and should not be used.
 */
export function getComBoxClientConfigInfo() {
  return getClientInfo()?.comBoxClientConfigInfo;
}

export function findBranch(branchId: string) {
  return getBranchesOfClient().find((branch) => branch.id === branchId);
}

/**
 * Finds a live branch of the current client by its branch **number**.
 *
 * Devices store the branch number, not the branch id, in `branch` and `homeLocation`,
 * so this is the lookup to use when resolving a device's location. Returns undefined
 * when the number names no live branch of the client, which is also the case for
 * clients that define no branches at all.
 */
export function findBranchByNumber(branchNumber?: string | number | null) {
  if (branchNumber === null || branchNumber === undefined || branchNumber === "") {
    return undefined;
  }

  return getBranchesOfClient().find(
    (branch) => branch.branchNumber.toString() === branchNumber.toString()
  );
}

/**
 * Countries to fall back on when an address states none of its own, most specific first.
 *
 * Feeds `countryFallbacks` on a `PickerAddress` / `GeocodeAddress`, which
 * `resolveCountryFilter` walks to pick the `components=country:` filter for a lookup
 * (ticket 2609015). Branch before company, decided with Taimoor on 2026-09-07: a branch is the
 * nearer statement of where the work happens, and a client working across a border has branches
 * on both sides while its company address names only one.
 *
 * The values are returned **raw**, not normalized: `resolveCountryFilter` does that, and it
 * skips a candidate it cannot read, so a branch holding `"Deutschalnd"` falls through to the
 * company rather than suppressing the filter altogether.
 *
 * @param branchId - a project's `clientData.branch`, which is a branch **id**. Measured over 353
 *   production projects on 2026-09-07: 173 of 173 non-empty values are branch ids and none is a
 *   branch number, unlike the `branch` a *device* carries. Passing a number here would match
 *   nothing and fall silently through to the company.
 * @returns the branch country then the company country; either may be absent.
 */
export function getAddressCountryFallbacks(
  branchId?: string | null
): (string | null | undefined)[] {
  return [
    branchId ? findBranch(branchId)?.countryCode : undefined,
    getClientInfo()?.country,
  ];
}

/**
 * The same fallbacks for a **user**, who belongs to a list of branches rather than one.
 *
 * A user's branch country is used **only when every branch they belong to agrees** (decision
 * with Taimoor, 2026-09-07). A hard `components=country:` filter naming the wrong country turns
 * a real address into ZERO_RESULTS, which is recorded as "this address has no location", so a
 * user spanning a German and a Polish branch must fall through to the company rather than guess.
 *
 * An empty `branches` means "all branches" rather than none, and that is not a single country
 * either, so it also falls through.
 *
 * @param branchIds - the user's `branches`, a list of branch ids.
 */
export function getUserAddressCountryFallbacks(
  branchIds?: string[] | null
): (string | null | undefined)[] {
  const companyCountry = getClientInfo()?.country;

  if (!branchIds || branchIds.length === 0) {
    return [companyCountry];
  }

  const countries = branchIds.map(
    (branchId) => findBranch(branchId)?.countryCode ?? ""
  );
  const agreed = countries.every((country) => country === countries[0]);

  return agreed ? [countries[0], companyCountry] : [companyCountry];
}

export function findBranchOfMemberClient(branchId: string, memberClient: Client) {
  return getBranchesOfMemberClient(memberClient).find((branch) => branch.id === branchId);
}

export function doesClientHaveApp() {
  return getClientInfo()?.hasApp === true;
}

export function isIresClient(skipIgnoreIresAdminOrIresClientCheck?: boolean) {
  if (!skipIgnoreIresAdminOrIresClientCheck &&
    getIgnoreIresAdminOrIresClientCheck()) {
    return false;
  }
  
  return getClientInfo()?.isIresClient ?? false;
}

export function isExternalSystem() {
  return getClientInfo()?.isExternalSystem ?? false;
}

export function syncsProjectWithErp() {
  return getClientInfo()?.syncsProjectWithErp ?? false;
}

export function getTargetAppVersion() {
  return getClientInfo()?.targetAppVersion ?? 0;
}

export function getProjectManagement(): ProjectManagement {
  return getClientInfo()?.projectManagement ?? ProjectManagement.DEFAULT;
}

export function shouldHideSerialNumber() {
  return HyDryDeviceSettingsModule.hyDryDeviceSettings.alwaysShowSerialNumber === false;
}

export function getCurrentIresClientType() {
  return getClientInfo()?.iresClientType ?? IresClientType.Other;
}

export function getGeoEncodingAccess() {
  const systemConfiguration = SystemConfigurationModule.systemConfiguration;

  return !systemConfiguration.isDefaultSystemConfiguration
    ? systemConfiguration.enableGeoEncoding
    : getClientInfo()?.enableGeoEncoding ?? false;
}

/**
 * TRACKING: whether this client may use live positions (Hans, ticket 2609016).
 *
 * Requires KARTE, and enforces it here as well as in the settings screen, so a value left behind by
 * an older write cannot outlive the feature it depends on.
 *
 * Nothing consumes this yet: it is declared and configurable, the same posture the address format
 * setting shipped in.
 */
export function getTrackingAccess() {
  const systemConfiguration = SystemConfigurationModule.systemConfiguration;

  if (!getGeoEncodingAccess()) {
    return false;
  }

  return !systemConfiguration.isDefaultSystemConfiguration
    ? systemConfiguration.enableTracking ?? false
    : false;
}

/**
 * Whether this client works with addresses on the map: the pin beside every address, the picker it
 * opens, and FAHRT2 in IRES Work (Hans, ticket 2609016).
 *
 * Einstellungen Niederlassung, so a client decides for itself, and **off unless asked for**.
 * Geocoding runs server-side regardless, so switching it on reveals positions the records already
 * carry rather than starting from nothing.
 */
export function getAddressGeocodingAccess() {
  const companySettings = CompanySettingsModule.companySettings;

  return !companySettings.areDefaultCompanySettings
    ? companySettings.enableAddressGeocoding ?? false
    : false;
}

export function getClientLanguage() {
  const companySettings = CompanySettingsModule.companySettings;

  return !companySettings.areDefaultCompanySettings
    ? companySettings.language
    : getClientInfo()?.language ?? DEFAULT_LOCALE;
}

export function getLangugageSettingsForReports() {
  const companySettings = CompanySettingsModule.companySettings;

  return !companySettings.areDefaultCompanySettings
    ? companySettings.usePrimaryLanguageForReports
    : getClientInfo()?.usePrimaryLanguageForReports ?? true;
}

export function getAllDevicesManagementAccess() {
  const systemConfiguration = SystemConfigurationModule.systemConfiguration;

  return !systemConfiguration.isDefaultSystemConfiguration
    ? systemConfiguration.enableAllDevicesManagement
    : getClientInfo()?.enableAllDevicesManagement ?? false;
}

export function getShowDeviceManagementInCockpitAccess() {
  const systemConfiguration = SystemConfigurationModule.systemConfiguration;
  
  return !systemConfiguration.isDefaultSystemConfiguration
    ? systemConfiguration.enableDeviceManagementTile
    : getClientInfo()?.enableDeviceManagementTile ?? false;
}

export function getTimeTrackingAccess() {
  const systemConfiguration = SystemConfigurationModule.systemConfiguration;
  
  return !systemConfiguration.isDefaultSystemConfiguration
    ? systemConfiguration.enableTimeTracking
    : getClientInfo()?.enableTimeTracking ?? false;
}

export function getDeviceCheckAccess() {
  const systemConfiguration = SystemConfigurationModule.systemConfiguration;
  
  return !systemConfiguration.isDefaultSystemConfiguration
    ? systemConfiguration.enableDeviceCheckManagement
    : false;
}

export function getAiPredictionsAccess() {
  const systemConfiguration = SystemConfigurationModule.systemConfiguration;
  
  return !systemConfiguration.isDefaultSystemConfiguration
    ? systemConfiguration.enableAiPredictions
    : false;
}

export function getRenoActivationScopeAccess(): RenoActivationScope {
  const systemConfiguration = SystemConfigurationModule.systemConfiguration;

  return !systemConfiguration.isDefaultSystemConfiguration
    ? systemConfiguration.renoActivationScope ?? RenoActivationScope.ACTIVE_AND_NEW_ONLY
    : RenoActivationScope.ACTIVE_AND_NEW_ONLY;
}

/**
 * Returns whether billing catalog management (Leistungslisten) is enabled
 * for the current client via SystemConfiguration.
 */
export function getBillingCatalogsAccess() {
  const systemConfiguration = SystemConfigurationModule.systemConfiguration;

  return !systemConfiguration.isDefaultSystemConfiguration
    ? systemConfiguration.enableBillingCatalogs
    : false;
}

/**
 * Whether the personal chat is switched on for the current client in Systemkonfiguration
 * (ticket 2610004). Off when unset. No Office consumer yet; IRES Work will read the same field.
 */
export function getPersonalChatAccess() {
  const systemConfiguration = SystemConfigurationModule.systemConfiguration;

  return !systemConfiguration.isDefaultSystemConfiguration
    ? systemConfiguration.enablePersonalChat ?? false
    : false;
}

/**
 * Whether the project chat is switched on for the current client in Systemkonfiguration
 * (ticket 2610004). Off when unset. No Office consumer yet; IRES Work will read the same field.
 */
export function getProjectChatAccess() {
  const systemConfiguration = SystemConfigurationModule.systemConfiguration;

  return !systemConfiguration.isDefaultSystemConfiguration
    ? systemConfiguration.enableProjectChat ?? false
    : false;
}

/**
 * Who sees Mira in this client (#2610141): Nein / Nur IRES / Ja, from its Systemkonfiguration. Nur IRES
 * when nothing is stored, which is what every client had before the setting existed. Read by
 * `miraAccess()`; the store is reactive, so a gate built on it follows a change.
 */
export function getMiraModeAccess(): MiraMode {
  const systemConfiguration = SystemConfigurationModule.systemConfiguration;

  return !systemConfiguration.isDefaultSystemConfiguration
    ? toMiraMode(systemConfiguration.miraMode)
    : DEFAULT_MIRA_MODE;
}

export function getVehicleManagementAccess() {
  const systemConfiguration = SystemConfigurationModule.systemConfiguration;
  
  return !systemConfiguration.isDefaultSystemConfiguration
    ? systemConfiguration.enableVehicleManagement
    : getClientInfo()?.enableVehicleManagement ?? false;
}

export function getSchedulingAccess() {  
  const systemConfiguration = SystemConfigurationModule.systemConfiguration;
  
  return !systemConfiguration.isDefaultSystemConfiguration
    ? systemConfiguration.enableScheduling
    : getClientInfo()?.enableScheduling ?? false;
}

export function setClientInfo(clientInfo: ClientInfo) {
  LocalSettingsModule.setComBoxHumidityUnitFromClientInfo(clientInfo);
  LocalSettingsModule.setHyDryMessageSettings(clientInfo.hyDryMessageSettings);
  LocalSettingsModule.setMinProgramChainDurationInMinutes(
    clientInfo.minProgramChainDurationInMin ?? 30,
  );
  
  setString(KEY_CLIENT_INFO, JSON.stringify(clientInfo));
}

function extractTargetAppVersion(client: Client) {
  const targetAppApk = client.targetAppApk;
  
  if (!targetAppApk) {
    return 0;
  }
  
  return Number.parseInt(targetAppApk.replace(/[^0-9]/g, ""));
}

function updateClientInfo(client: Client) {
  const branches = [];
  
  if (client.branches) {
    const keys = Object.keys(client.branches);
    
    for (const key of keys) {
      branches.push(client.branches[key] as Branch);
    }
  }

  const clientInfo: ClientInfo = {
    hyDryMessageSettings:
      client.hyDryMessageSettings ?? createDefaultHyDryMessagesSettings(),
    erpIntegration: client.erpSettings?.integration ?? ErpIntegration.NONE,
    projectSupervisorSource: client.userSettings?.projectSupervisorSource,
    strictBranchFilters: client.userSettings?.strictBranchFilters ?? false,
    projectManagement: client.projectManagement,
    hasWorkAccess: client.products?.work === true,
    iresClientType: getIresClientType(client.id),
    branches: branches,
    targetAppVersion: extractTargetAppVersion(client),
    hasApp: client.hasApp === true,
    isExternalSystem: false,
    targetClientId: client.id,
    isIresClient: client.clientType === ClientType.IRES,
    clientName: client.clientName,
    shortName: client.shortName ?? null,
    logoFile: client.logoFile ?? null,
    zipCode: client.zip,
    companyName: client.companyName,
    city: client.city,
    street: client.street,
    houseNumber: client.houseNumber,
    // Carried so a consumer of the company address can use the position IRES Cloud already
    // geocoded for it instead of paying for the same lookup again.
    coordinates: client.coordinates,
    minProgramChainDurationInMin: client.minProgramChainDurationInMin ?? 30,
    syncsProjectWithErp: !!client.syncProjectsWith?.erpSystems,
    
    comBoxClientConfigInfo: client.comBoxSettings?.clientConfig ?? undefined,
    
    comBoxHumidityUnit:
      client.comBoxSettings?.clientConfig?.humidityUnit ??
      client.comBoxSettings?.humidityUnit ??
      ComBoxHumidityUnit.RELATIVE_HUMIDITY,
    
    visibleTo: client.visibleTo ?? [],
    
    ignoreIresAdminOrIresClientCheck: client.ignoreIresAdminOrIresClientCheck,
    isTestSystem: client.isTestSystem,
    isActive: client.isActive,

    deviceSettings: client.deviceSettings ?? createDefaultDeviceSettingsDeprecated(),
    timeTrackingConfigurations: client.timeTrackingConfigurations ?? createrDefaultTimeTrackingConfigurations(),

    enableGeoEncoding: client.enableGeoEncoding,
    enableVehicleManagement: client.enableVehicleManagement,
    enableScheduling: client.enableScheduling,
    enableAllDevicesManagement: client.enableAllDevicesManagement,
    enableDeviceManagementTile: client.enableDeviceManagementTile,
    enableTimeTracking: client.enableTimeTracking,

    language: client.language ?? DEFAULT_LOCALE,
    usePrimaryLanguageForReports: client.usePrimaryLanguageForReports ?? true,

    hasAdministrationCapabilities: client.hasAdministrationCapabilities ?? false,
    memberClientIdForSettings: client.memberClientIdForSettings,
    isOperationalClient: client.isOperationalClient ?? true,

    memberClientIds: client.memberClientIds ?? [],
    memberClientsConfiguration: client.memberClientsConfiguration ?? undefined,

    clientConfiguration: client.clientConfiguration,
    administratorClientId: client.administratorClientId ?? undefined,

    companyEmailAddress: client.companyEmailAddress ?? undefined,
    companyIdentifier: client.companyIdentifier ?? undefined,
    country: client.country ?? undefined,

    testMetadata: client.testMetadata ?? undefined,
    targetRoomInfoConfigVersion: client.targetRoomInfoConfigVersion ?? undefined,
  };

  ClientModule.setIgnoreIresAdminOrIresClientCheck(client.ignoreIresAdminOrIresClientCheck ?? false);

  const user = UserModule.activeUser;
  const companySettings = CompanySettingsModule.companySettings;

  if (
    user &&
    user.language
  ) {
    changeLanguageTo(user.language);
  } else if (
    companySettings &&
    !companySettings.areDefaultCompanySettings
  ) {
    changeLanguageTo(companySettings.language);
  } 
  else if (clientInfo.language) {
    changeLanguageTo(clientInfo.language)
  }

  // Apply the user's saved dark-mode preference (if any). This overrides the
  // per-browser localStorage/OS default so the choice follows the user across
  // devices. `false` is a valid saved value, hence the explicit undefined check.
  if (user && user.darkMode !== undefined) {
    changeThemeTo(user.darkMode);
  }

  setClientInfo(clientInfo);
}

export function updateIgnoreIresAdminOrIresClientCheck(val: boolean) {
  ClientModule.setIgnoreIresAdminOrIresClientCheck(val);
 
  return db.doc(getClientRef()).set(
    {
      ignoreIresAdminOrIresClientCheck: val,
    },
    { merge: true },
  );
}

/**
 * Asynchronously updates the role settings configuration for a list of member clients and the overall configuration for member clients in the firebase database.
 * This function first prepares a batch update to modify each member client's individual configuration based on whether they should define their own role settings.
 * The determination is made based on the `defineOwnRoleSettings` flag and whether the client is listed in `memberClientsWithOwnRoleSettings`.
 * It then updates the global configuration for all member clients to reflect the new settings.
 * All updates are performed as a single batch operation to ensure atomicity.
 *
 * @param {string[]} memberClientIds - An array of strings representing the unique identifiers of the member clients whose configurations are to be updated.
 * @param {MemberClientsConfiguration} memberClientsConfiguration - An object containing the new configuration settings for the member clients.
 *    This includes a flag indicating whether clients can define their own role settings and a list of client IDs for which this is true.
 *    The structure is as follows:
 *    {
 *      defineOwnRoleSettings: boolean,
 *      memberClientsWithOwnRoleSettings: string[]
 *    }
 * 
 * @returns {Promise<boolean>} A promise that resolves to `true` upon successful completion of the batch update. 
 * The promise does not explicitly return any data from the batch commit operation, but signifies successful execution of the updates.
 *
 * @example
 * // Call this function with a list of member client IDs and the new configuration settings
 * await updateMemberClientsRoleSettings(['client1', 'client2'], {
 *   defineOwnRoleSettings: true,
 *   memberClientsWithOwnRoleSettings: ['client2']
 * });
 */
export async function updateMemberClientsRoleSettings(
  memberClientIds: string[],
  memberClientsConfiguration: MemberClientsConfiguration
) {
  const batch = db.batch();

  // Member clients must always use the master's role settings — never allow defineOwnRoleSettings = true.
  memberClientIds.forEach((memberClientId) => {
    const memberClientRef = db.doc(getClientRefById(memberClientId));
    batch.update(memberClientRef, `clientConfiguration.defineOwnRoleSettings`, false);
  });

  const clientRef = db.doc(getClientRef());
  batch.set(clientRef, { memberClientsConfiguration: memberClientsConfiguration }, { merge: true });

  await batch.commit();

  return true;
}

/**
 * Asynchronously updates the system configuration for a set of member clients based on the provided configuration settings.
 * This function logs the current configuration, prepares a batch database operation to update each member client's ability
 * to define their own system configuration, and updates a centralized document with the new overall member clients configuration.
 * The decision to allow a member client to define their own system configuration is based on the `defineOwnSystemConfiguration` setting
 * and a list specifying which member clients have this capability.
 * 
 * The updates are made by setting the `defineOwnSystemConfiguration` property for each specified member client in their individual
 * `clientConfiguration` document. A global update is also performed on a central reference document to ensure consistency across
 * the system. These updates are committed as a single batch operation to ensure atomicity and reduce the risk of partial updates.
 *
 * @param {string[]} memberClientIds - An array containing the IDs of member clients whose system configurations are to be updated.
 * @param {MemberClientsConfiguration} memberClientsConfiguration - An object containing the new system configuration settings.
 *    This includes a boolean flag indicating whether clients can define their own system configurations, and a list of client
 *    IDs that are permitted to do so. Expected object structure:
 *    {
 *      defineOwnSystemConfiguration: boolean,
 *      memberClientsWithOwnSystemConfiguration: string[]
 *    }
 *
 * @returns {Promise<boolean>} A promise that resolves to `true` after the batch operation has successfully completed,
 *    indicating that all updates were applied as intended. If the operation fails, the promise will reject with an error.
 *
 * @example
 * // Example usage
 * await updateMemberClientsSystemConfiguration(['client1', 'client2'], {
 *   defineOwnSystemConfiguration: true,
 *   memberClientsWithOwnSystemConfiguration: ['client2']
 * });
 */
export async function updateMemberClientsSystemConfiguration(
  memberClientIds: string[],
  memberClientsConfiguration: MemberClientsConfiguration
) {
  const batch = db.batch();

  memberClientIds.forEach((memberClientId) => {
    const memberClientRef = db.doc(getClientRefById(memberClientId));
    const defineOwnSystemConfiguration = memberClientsConfiguration.defineOwnSystemConfiguration;
    const shouldDefineOwnSystemConfiguration = !defineOwnSystemConfiguration
      ? false
      : memberClientsConfiguration.memberClientsWithOwnSystemConfiguration.includes(memberClientId);

    // Update only the defineOwnSystemConfiguration in clientConfiguration
    batch.update(memberClientRef, `clientConfiguration.defineOwnSystemConfiguration`, shouldDefineOwnSystemConfiguration);
  });

  const clientRef = db.doc(getClientRef());
  batch.set(clientRef, { memberClientsConfiguration: memberClientsConfiguration }, { merge: true });

  await batch.commit();

  return true;
}

/**
 * Asynchronously updates the report settings for a set of member clients based on the provided configuration settings.
 * This function logs the current configuration, prepares a batch database operation to update each member client's ability
 * to define their own report settings, and updates a centralized document with the new overall member clients report settings configuration.
 * The decision to allow a member client to define their own report settings is based on the `defineOwnReportSettings` setting
 * and a list specifying which member clients have this capability.
 * 
 * The updates are made by setting the `defineOwnReportSettings` property for each specified member client in their individual
 * `clientConfiguration` document. A global update is also performed on a central reference document to ensure consistency across
 * the system. These updates are committed as a single batch operation to ensure atomicity and reduce the risk of partial updates.
 *
 * @param {string[]} memberClientIds - An array containing the IDs of member clients whose report settings are to be updated.
 * @param {MemberClientsConfiguration} memberClientsConfiguration - An object containing the new report settings configuration.
 *    This includes a boolean flag indicating whether clients can define their own report settings, and a list of client
 *    IDs that are permitted to do so. Expected object structure:
 *    {
 *      defineOwnReportSettings: boolean,
 *      memberClientsWithOwnReportSettings: string[]
 *    }
 *
 * @returns {Promise<boolean>} A promise that resolves to `true` after the batch operation has successfully completed,
 *    indicating that all updates were applied as intended. If the operation fails, the promise will reject with an error.
 *
 * @example
 * // Example usage
 * await updateMemberClientsReportSettings(['client1', 'client2'], {
 *   defineOwnReportSettings: true,
 *   memberClientsWithOwnReportSettings: ['client2']
 * });
 */
 export async function updateMemberClientsReportSettings(
  memberClientIds: string[],
  memberClientsConfiguration: MemberClientsConfiguration
) {
  const batch = db.batch();

  memberClientIds.forEach((memberClientId) => {
    const memberClientRef = db.doc(getClientRefById(memberClientId));
    const defineOwnReportSettings = memberClientsConfiguration.defineOwnReportSettings;
    const shouldDefineOwnReportSettings = !defineOwnReportSettings
      ? false
      : memberClientsConfiguration.memberClientsWithOwnReportSettings.includes(memberClientId);

    // Update only the defineOwnReportSettings in clientConfiguration
    batch.update(memberClientRef, `clientConfiguration.defineOwnReportSettings`, shouldDefineOwnReportSettings);
  });

  const clientRef = db.doc(getClientRef());
  batch.set(clientRef, { memberClientsConfiguration: memberClientsConfiguration }, { merge: true });

  await batch.commit();

  return true;
}

/**
 * Asynchronously updates the project management settings for a set of member clients based on the provided configuration settings.
 * This function logs the current configuration, prepares a batch database operation to update each member client's ability
 * to manage their own projects, and updates a centralized document with the new overall member clients project management settings configuration.
 * The decision to allow a member client to manage their own projects is based on the `defineOwnProjectManagementSettings` setting
 * and a list specifying which member clients have this capability.
 * 
 * The updates are made by setting the `defineOwnProjectManagementSettings` property for each specified member client in their individual
 * `clientConfiguration` document. A global update is also performed on a central reference document to ensure consistency across
 * the system. These updates are committed as a single batch operation to ensure atomicity and reduce the risk of partial updates.
 *
 * @param {string[]} memberClientIds - An array containing the IDs of member clients whose project management settings are to be updated.
 * @param {MemberClientsConfiguration} memberClientsConfiguration - An object containing the new project management settings configuration.
 *    This includes a boolean flag indicating whether clients can manage their own projects, and a list of client
 *    IDs that are permitted to do so. Expected object structure:
 *    {
 *      defineOwnProjectManagementSettings: boolean,
 *      memberClientsWithOwnProjectManagementSettings: string[]
 *    }
 *
 * @returns {Promise<boolean>} A promise that resolves to `true` after the batch operation has successfully completed,
 *    indicating that all updates were applied as intended. If the operation fails, the promise will reject with an error.
 *
 * @example
 * // Example usage
 * await updateMemberClientsProjectManagementSettings(['client1', 'client2'], {
 *   defineOwnProjectManagementSettings: true,
 *   memberClientsWithOwnProjectManagementSettings: ['client2']
 * });
 */
export async function updateMemberClientsProjectManagementSettings(
  memberClientIds: string[],
  memberClientsConfiguration: MemberClientsConfiguration
) {
  const batch = db.batch();

  memberClientIds.forEach((memberClientId) => {
    const memberClientRef = db.doc(getClientRefById(memberClientId));
    const canDefineOwnProjectManagementSettings = memberClientsConfiguration.defineOwnProjectManagementSettings;
    const shouldDefineOwnProjectManagementSettings = !canDefineOwnProjectManagementSettings
      ? false
      : memberClientsConfiguration.memberClientsWithOwnProjectManagementSettings.includes(memberClientId);

    // Update only the defineOwnProjectManagementSettings in clientConfiguration
    batch.update(memberClientRef, `clientConfiguration.defineOwnProjectManagementSettings`, shouldDefineOwnProjectManagementSettings);
  });

  const clientRef = db.doc(getClientRef());
  batch.set(clientRef, { memberClientsConfiguration: memberClientsConfiguration }, { merge: true });

  await batch.commit();

  return true;
}

/**
 * Asynchronously updates the device settings configuration for a set of member clients based on the provided configuration settings.
 * This function logs the current configuration, prepares a batch database operation to update each member client's ability
 * to define their own device settings, and updates a centralized document with the new overall member clients device settings configuration.
 * The decision to allow a member client to define their own device settings is based on the `defineOwnAllDevicesSettings` setting
 * and a list specifying which member clients have this capability.
 * 
 * The updates are made by setting the `defineOwnAllDevicesSettings` property for each specified member client in their individual
 * `clientConfiguration` document. A global update is also performed on a central reference document to ensure consistency across
 * the system. These updates are committed as a single batch operation to ensure atomicity and reduce the risk of partial updates.
 *
 * @param {string[]} memberClientIds - An array containing the IDs of member clients whose device settings are to be updated.
 * @param {MemberClientsConfiguration} memberClientsConfiguration - An object containing the new device settings configuration.
 *    This includes a boolean flag indicating whether clients can define their own device settings, and a list of client
 *    IDs that are permitted to do so. Expected object structure:
 *    {
 *      defineOwnAllDevicesSettings: boolean,
 *      memberClientsWithOwnAllDevicesSettings: string[]
 *    }
 *
 * @returns {Promise<boolean>} A promise that resolves to `true` after the batch operation has successfully completed,
 *    indicating that all updates were applied as intended. If the operation fails, the promise will reject with an error.
 *
 * @example
 * // Example usage
 * await updateMemberClientsAllDevicesSettings(['client1', 'client2'], {
 *   defineOwnAllDevicesSettings: true,
 *   memberClientsWithOwnAllDevicesSettings: ['client2']
 * });
 */
export async function updateMemberClientsAllDevicesSettings(
  memberClientIds: string[],
  memberClientsConfiguration: MemberClientsConfiguration
) {
  const batch = db.batch();

  memberClientIds.forEach((memberClientId) => {
    const memberClientRef = db.doc(getClientRefById(memberClientId));
    const canDefineOwnAllDevicesSettings = memberClientsConfiguration.defineOwnAllDevicesSettings;
    const shouldDefineOwnAllDevicesSettings = !canDefineOwnAllDevicesSettings
      ? false
      : memberClientsConfiguration.memberClientsWithOwnAllDevicesSettings.includes(memberClientId);

    // Update only the defineOwnAllDevicesSettings in clientConfiguration
    batch.update(memberClientRef, `clientConfiguration.defineOwnAllDevicesSettings`, shouldDefineOwnAllDevicesSettings);
  });

  const clientRef = db.doc(getClientRef());
  batch.set(clientRef, { memberClientsConfiguration: memberClientsConfiguration }, { merge: true });

  await batch.commit();

  return true;
}

/**
 * Asynchronously updates the user settings configuration for a set of member clients based on the provided configuration settings.
 * This function logs the current configuration, prepares a batch database operation to update each member client's ability
 * to define their own user settings, and updates a centralized document with the new overall member clients user settings configuration.
 * The decision to allow a member client to define their own user settings is based on the `defineOwnUserSettings` setting
 * and a list specifying which member clients have this capability.
 * 
 * The updates are made by setting the `defineOwnUserSettings` property for each specified member client in their individual
 * `clientConfiguration` document. A global update is also performed on a central reference document to ensure consistency across
 * the system. These updates are committed as a single batch operation to ensure atomicity and reduce the risk of partial updates.
 *
 * @param {string[]} memberClientIds - An array containing the IDs of member clients whose user settings are to be updated.
 * @param {MemberClientsConfiguration} memberClientsConfiguration - An object containing the new user settings configuration.
 *    This includes a boolean flag indicating whether clients can define their own user settings, and a list of client
 *    IDs that are permitted to do so. Expected object structure:
 *    {
 *      defineOwnUserSettings: boolean,
 *      memberClientsWithOwnUserSettings: string[]
 *    }
 *
 * @returns {Promise<boolean>} A promise that resolves to `true` after the batch operation has successfully completed,
 *    indicating that all updates were applied as intended. If the operation fails, the promise will reject with an error.
 *
 * @example
 * // Example usage
 * await updateMemberClientsUserSettings(['client1', 'client2'], {
 *   defineOwnUserSettings: true,
 *   memberClientsWithOwnUserSettings: ['client2']
 * });
 */
export async function updateMemberClientsUserSettings(
  memberClientIds: string[],
  memberClientsConfiguration: MemberClientsConfiguration
) {
  const batch = db.batch();

  memberClientIds.forEach((memberClientId) => {
    const memberClientRef = db.doc(getClientRefById(memberClientId));
    const defineOwnUserSettings = memberClientsConfiguration.defineOwnUserSettings;
    const shouldDefineOwnUserSettings = !defineOwnUserSettings
      ? false
      : memberClientsConfiguration.memberClientsWithOwnUserSettings.includes(memberClientId);

    // Update only the defineOwnUserSettings in clientConfiguration
    batch.update(memberClientRef, `clientConfiguration.defineOwnUserSettings`, shouldDefineOwnUserSettings);
  });

  const clientRef = db.doc(getClientRef());
  batch.set(clientRef, { memberClientsConfiguration: memberClientsConfiguration }, { merge: true });

  await batch.commit();

  return true;
}

/**
 * Asynchronously updates the HyDry messages settings for a set of member clients based on the provided configuration settings.
 * This function logs the current configuration, prepares a batch database operation to update each member client's ability
 * to define their own HyDry messages settings, and updates a centralized document with the new overall member clients configuration.
 * The decision to allow a member client to define their own HyDry messages settings is based on the `defineOwnHyDryMessagesSettings`
 * setting and a list specifying which member clients have this capability.
 * 
 * The updates are made by setting the `defineOwnHyDryMessagesSettings` property for each specified member client in their individual
 * `clientConfiguration` document. A global update is also performed on a central reference document to ensure consistency across
 * the system. These updates are committed as a single batch operation to ensure atomicity and reduce the risk of partial updates.
 *
 * @param {string[]} memberClientIds - An array containing the IDs of member clients whose HyDry messages settings are to be updated.
 * @param {MemberClientsConfiguration} memberClientsConfiguration - An object containing the new HyDry messages settings configuration.
 *    This includes a boolean flag indicating whether clients can define their own HyDry messages settings, and a list of client
 *    IDs that are permitted to do so. Expected object structure:
 *    {
 *      defineOwnHyDryMessagesSettings: boolean,
 *      memberClientsWithOwnHyDryMessagesSettings: string[]
 *    }
 *
 * @returns {Promise<boolean>} A promise that resolves to `true` after the batch operation has successfully completed,
 *    indicating that all updates were applied as intended. If the operation fails, the promise will reject with an error.
 *
 * @example
 * // Example usage
 * await updateMemberClientsHyDryMessagesSettings(['client1', 'client2'], {
 *   defineOwnHyDryMessagesSettings: true,
 *   memberClientsWithOwnHyDryMessagesSettings: ['client2']
 * });
 */
export async function updateMemberClientsHyDryMessagesSettings(
  memberClientIds: string[],
  memberClientsConfiguration: MemberClientsConfiguration
) {
  const batch = db.batch();

  memberClientIds.forEach((memberClientId) => {
    const memberClientRef = db.doc(getClientRefById(memberClientId));
    const defineOwnHyDryMessagesSettings = memberClientsConfiguration.defineOwnHyDryMessagesSettings;
    const shouldDefineOwnHyDryMessagesSettings = !defineOwnHyDryMessagesSettings
      ? false
      : memberClientsConfiguration.memberClientsWithOwnHyDryMessagesSettings.includes(memberClientId);

    // Update only the defineOwnHyDryMessagesSettings in clientConfiguration
    batch.update(memberClientRef, `clientConfiguration.defineOwnHyDryMessagesSettings`, shouldDefineOwnHyDryMessagesSettings);
  });

  const clientRef = db.doc(getClientRef());
  batch.set(clientRef, { memberClientsConfiguration: memberClientsConfiguration }, { merge: true });

  await batch.commit();

  return true;
}

/**
 * Asynchronously updates the HyDry device settings for a set of member clients based on the provided configuration settings.
 * This function logs the current configuration, prepares a batch database operation to update each member client's ability
 * to define their own HyDry device settings, and updates a centralized document with the new overall member clients HyDry device settings configuration.
 * The decision to allow a member client to define their own HyDry device settings is based on the `defineOwnHyDryDeviceSettings` setting
 * and a list specifying which member clients have this capability.
 * 
 * The updates are made by setting the `defineOwnHyDryDeviceSettings` property for each specified member client in their individual
 * `clientConfiguration` document. A global update is also performed on a central reference document to ensure consistency across
 * the system. These updates are committed as a single batch operation to ensure atomicity and reduce the risk of partial updates.
 *
 * @param {string[]} memberClientIds - An array containing the IDs of member clients whose HyDry device settings are to be updated.
 * @param {MemberClientsConfiguration} memberClientsConfiguration - An object containing the new HyDry device settings configuration.
 *    This includes a boolean flag indicating whether clients can define their own HyDry device settings, and a list of client
 *    IDs that are permitted to do so. Expected object structure:
 *    {
 *      defineOwnHyDryDeviceSettings: boolean,
 *      memberClientsWithOwnHyDryDeviceSettings: string[]
 *    }
 *
 * @returns {Promise<boolean>} A promise that resolves to `true` after the batch operation has successfully completed,
 *    indicating that all updates were applied as intended. If the operation fails, the promise will reject with an error.
 *
 * @example
 * // Example usage
 * await updateMemberClientsHyDryDeviceSettings(['client1', 'client2'], {
 *   defineOwnHyDryDeviceSettings: true,
 *   memberClientsWithOwnHyDryDeviceSettings: ['client2']
 * });
 */
 export async function updateMemberClientsHyDryDeviceSettings(
  memberClientIds: string[],
  memberClientsConfiguration: MemberClientsConfiguration
) {
  const batch = db.batch();

  memberClientIds.forEach((memberClientId) => {
    const memberClientRef = db.doc(getClientRefById(memberClientId));
    const defineOwnHyDryDeviceSettings = memberClientsConfiguration.defineOwnHyDryDeviceSettings;
    const shouldDefineOwnHyDryDeviceSettings = !defineOwnHyDryDeviceSettings
      ? false
      : memberClientsConfiguration.memberClientsWithOwnHyDryDeviceSettings.includes(memberClientId);

    // Update only the defineOwnHyDryDeviceSettings in clientConfiguration
    batch.update(memberClientRef, `clientConfiguration.defineOwnHyDryDeviceSettings`, shouldDefineOwnHyDryDeviceSettings);
  });

  const clientRef = db.doc(getClientRef());
  batch.set(clientRef, { memberClientsConfiguration: memberClientsConfiguration }, { merge: true });

  await batch.commit();

  return true;
}

/**
 * Asynchronously updates the company settings for a set of member clients based on the provided configuration settings.
 * This function logs the current configuration, prepares a batch database operation to update each member client's ability
 * to define their own company settings, and updates a centralized document with the new overall member clients company settings configuration.
 * The decision to allow a member client to define their own company settings is based on the `defineOwnCompanySettings` setting
 * and a list specifying which member clients have this capability.
 * 
 * The updates are made by setting the `defineOwnCompanySettings` property for each specified member client in their individual
 * `clientConfiguration` document. A global update is also performed on a central reference document to ensure consistency across
 * the system. These updates are committed as a single batch operation to ensure atomicity and reduce the risk of partial updates.
 *
 * @param {string[]} memberClientIds - An array containing the IDs of member clients whose company settings are to be updated.
 * @param {MemberClientsConfiguration} memberClientsConfiguration - An object containing the new company settings configuration.
 *    This includes a boolean flag indicating whether clients can define their own company settings, and a list of client
 *    IDs that are permitted to do so. Expected object structure:
 *    {
 *      defineOwnCompanySettings: boolean,
 *      memberClientsWithOwnCompanySettings: string[]
 *    }
 *
 * @returns {Promise<boolean>} A promise that resolves to `true` after the batch operation has successfully completed,
 *    indicating that all updates were applied as intended. If the operation fails, the promise will reject with an error.
 *
 * @example
 * // Example usage
 * await updateMemberClientsCompanySettings(['client1', 'client2'], {
 *   defineOwnCompanySettings: true,
 *   memberClientsWithOwnCompanySettings: ['client2']
 * });
 */
 export async function updateMemberClientsCompanySettings(
  memberClientIds: string[],
  memberClientsConfiguration: MemberClientsConfiguration
) {
  const batch = db.batch();

  memberClientIds.forEach((memberClientId) => {
    const memberClientRef = db.doc(getClientRefById(memberClientId));
    const defineOwnCompanySettings = memberClientsConfiguration.defineOwnCompanySettings;
    const shouldDefineOwnCompanySetting = !defineOwnCompanySettings
      ? false
      : memberClientsConfiguration.memberClientsWithOwnCompanySettings.includes(memberClientId);

    // Update only the defineOwnCompanySettings in clientConfiguration
    batch.update(memberClientRef, `clientConfiguration.defineOwnCompanySettings`, shouldDefineOwnCompanySetting);
  });

  const clientRef = db.doc(getClientRef());
  batch.set(clientRef, { memberClientsConfiguration: memberClientsConfiguration }, { merge: true });

  await batch.commit();

  return true;
}

export function updateEmailAddress(emailAddress: string) {
  return db.doc(getClientRef()).set(
    {
      email: emailAddress,
    },
    { merge: true },
  );
}

export function observeClient(onClientChange: (client: Client) => void) {
  return db.doc(getClientRef()).onSnapshot((snapshot) => {
    if (snapshot.exists) {
      const client = snapshot.data() as Client;
      
      if (client) {
        updateClientInfo(client);
        onClientChange(client);
      }
    }
  });
}

export async function getClient(clientId: string) {
  return (
    await db.collection("clients").doc(clientId).get()
  ).data() as Client;
}

export async function loadClient() {
  const client = (await db.doc(getClientRef()).get()).data() as Client;
  
  if (!client) {
    return null;
  }
  
  updateClientInfo(client);
  
  return client;
}

export async function updateClient(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  update: any,
) {
  await db.doc(getClientRef()).set(update, { merge: true });
}

/**
 * @deprecated saveComBoxClientConfig is deprecated and should not be used.
 */
export async function saveComBoxClientConfig(
  comBoxClientConfig: ComBoxClientConfig,
) {
  const comBoxClientConfigInfo = buildComBoxClientConfigInfo(
    comBoxClientConfig,
    "4.0.0",
  );
  
  await db.doc(getClientRef()).set(
    {
      comBoxSettings: {
        clientConfig: comBoxClientConfigInfo,
      },
    },
    { merge: true },
  );
  
  LocalSettingsModule.setComBoxHumidityUnit(
    comBoxClientConfigInfo.humidityUnit,
  );
  
  const clientInfo = getClientInfo();
  
  if (clientInfo) {
    clientInfo.comBoxClientConfigInfo = comBoxClientConfigInfo;
    setClientInfo(clientInfo);
  }
}

export function saveBranch(branch: Branch) {
  return updateClient({
    branches: {
      [branch.id]: branch,
    },
  });
}

/**
 * Retrieves the device settings for a client from the database. This function is deprecated
 * and should not be used in new code. It attempts to fetch device settings and defaults to
 * a predefined configuration if the settings do not exist or an error occurs.
 * 
 * @deprecated This function is deprecated. Use the updated method for fetching client device settings.
 * @returns {Promise<GenericDeviceSettingsDeprecated | null>} A promise that resolves to the device settings object or null if an error occurs.
 */
export async function getClientDeviceSettingsDeprecated() {
  try {
    const doc = await db.doc(getClientRef()).get();
    if (doc.data()) {
      const deviceSettings = (doc.data())?.deviceSettings;

      return deviceSettings
        ? {
          newCategoryAllowed: deviceSettings.isNewCategoryAllowed,
          newTemplateAllowed: deviceSettings.isNewTemplateAllowed,
          updateAllowed: deviceSettings.isUpdateAllowed
        }
        : createDefaultDeviceSettingsDeprecated();
    } else {
      return createDefaultDeviceSettingsDeprecated();
    }
  } catch (e) {
    return null;
  }
}

export async function getTimeTrackingConfigurations() {
  try {
    const doc = await db.doc(getClientRef()).get();
    if (doc.data()) {
      return (doc.data() as Client).timeTrackingConfigurations ?? createrDefaultTimeTrackingConfigurations();
    } else {
      return createrDefaultTimeTrackingConfigurations();
    }
  } catch (e) {
    return null;
  }
}

export async function getDailyTrackingSettings() {
  try {
    const doc = await db.doc(getClientRef()).get();
    if (doc.data()) {
      return (doc.data() as Client).dailyTimeTrackingSettings ?? createrDefaultDailyTrackingSettings();
    } else {
      return createrDefaultDailyTrackingSettings();
    }
  } catch (e) {
    return null;
  }
}

export async function saveTimeTrackingConfigurations(configurations: TimeTrackingConfigurations) {
  try {
   await updateClient({
      timeTrackingConfigurations: configurations
    })
    return true
  } catch (e) {
    return false
  }
}

export async function saveDailyTrackingSettings(
  settings: DailyTrackingSettings
) {
  try {
    await updateClient({
      dailyTimeTrackingSettings: settings,
    });
    return true;
  } catch (e) {
    return false;
  }
}

export function getClientName(client: Client | null) {
  if (!client) {
    return null;
  }

  if (client.shortName) {
    return client.shortName;
  }

  const clientName =
    !client.companyName || client.companyName === "" || client.companyName === DEFAULT_COMPANY_NAME
      ? client.clientName
      : client.companyName;

  return clientName;
}

/**
 * Every client, unfiltered, for an IRES reviewer who works across the whole fleet.
 *
 * Distinct from {@link getAllClients}, which is the master/member administrator view: that one narrows
 * to the caller's own member clients and returns nothing at all without administrator access, so it
 * cannot answer "which client owns this drying" for the RENO review queue.
 *
 * Readable because `isAllowedToReadCoreClientData` grants an `IRES`-typed client the whole collection,
 * which is also what lets the review stepper resolve one drying's owner. Around 385 documents, so the
 * caller should load this on demand rather than on mount.
 *
 * @returns Client id to display name, name ascending. Empty when the read is refused.
 */
export async function getReviewableClients(): Promise<Map<string, string>> {
  const names = new Map<string, string>();

  try {
    const snapshot = await db.collection("clients").get();
    const rows: Array<{ id: string; name: string }> = [];

    snapshot.forEach((doc) => {
      const name = getClientName(doc.data() as Client);

      // A client with neither short nor company name would show as an empty option.
      if (name) {
        rows.push({ id: doc.id, name });
      }
    });

    rows
      .sort((first, second) => first.name.localeCompare(second.name))
      .forEach((row) => names.set(row.id, row.name));
  } catch {
    // Leave it empty; the caller shows "no clients" rather than failing the screen.
  }

  return names;
}

export function storeAdministratorSession(administratorClientId: string) {
  const administratorUser = UserModule.activeUser;
  const administratorUserId = administratorUser?.id;
  const administratorRole = administratorUser?.role;

  if (administratorUserId) {
    localStorage.setItem('administratorUserId', administratorUserId || '');
    localStorage.setItem('administratorClientId', administratorClientId);
    localStorage.setItem('administratorRole', administratorRole || Role.ADMIN );
    localStorage.setItem(
      ADMINISTRATOR_SESSION_USER_KEY,
      JSON.stringify(filterOutUndefined(administratorUser)),
    );
  }

  return;
}

export function storeSharedUserSession() {
  const user = UserModule.activeUser;
  const userId = user?.id;
  const emailAddress = user?.emailAddress;

  if (userId && emailAddress) {
    localStorage.setItem('sharedClients', JSON.stringify(user.clients ?? []));
    localStorage.setItem('emailAddress', emailAddress);
    localStorage.setItem('userId', userId);
  }

  return;
}

/**
 * Switches the authenticated user to a member client using their email.
 * 
 * This function requests a custom token for the specified member client by calling a Firebase Cloud Function (`generateTokenForMemberClient`).
 * It then signs in the user using the generated token, allowing the user to switch between clients.
 * 
 * @param {string} memberClientEmail - The email address of the member client to switch to.
 * @param {string[]} memberClientIds - The list of member client IDs that the administrator is allowed to switch to.
 * @param {string} [administratorUserId] - Optional ID of the administrator user (if switching back to the origin).
 * @returns {Promise<LoginResult>} Returns `LoginResult.SUCCESS` if the switch was successful, otherwise returns `LoginResult.FAILURE`.
 * @throws {Error} If the token generation or sign-in process fails.
 */
export async function switchToMemberClient(
  memberClientEmail: string,
  memberClientIds: string[],
  administratorUserId?: string
) {
  const payload = filterOutUndefined({
    memberClientEmail: memberClientEmail,
    administratorClientId:
      localStorage.getItem("administratorClientId") ?? getClientId(),
    administratorUserId: administratorUserId ?? localStorage.getItem('administratorUserId') ?? undefined,
  });

  try {
    const generateTokenForMemberClient = functions.httpsCallable(
      "generateTokenForMemberClient"
    );

    // Request the custom token for the member client
    const { wasSuccessful, token } = (
      await generateTokenForMemberClient(payload)
    ).data;

    if (wasSuccessful && token) {
      // Sign in using the custom token
      await auth().signInWithCustomToken(token);

      if (!administratorUserId) {
        ClientModule.setIsAdministratorClientUser(true);
        ClientModule.setMemberClientIds(memberClientIds);

        const administratorRole = localStorage.getItem('administratorRole') as Role | null;
        const administratorSessionUser = getAdministratorSessionUser();
        const userToBeStored = buildSwitchedSessionUser(
          administratorSessionUser,
          localStorage.getItem('administratorUserId'),
          administratorRole,
        );

        storeActiveUser(userToBeStored);
      } else {
        ClientModule.clear();
      }

      return LoginResult.SUCCESS;
    } else {
      return LoginResult.FAILURE;
    }
  } catch (error) {
    // #2610066: a blocked or deleted member is refused with its reason in the error's details.
    const reason = toSignInRefusalReason((error as { details?: { reason?: unknown } } | null)?.details?.reason);

    return reason ? loginResultForRefusal(reason) : LoginResult.FAILURE;
  }
}

export async function updateBranchInformationForGroupIfNecessary(
  branch: Branch,
) {
  const administratorClientId = getAdministratorClientId();

  if (administratorClientId) {
    const updateBranchInformationForGroup = functions.httpsCallable(
      "updateBranchInformationForGroup"
    );

    const memberClientBranch = branch as MemberClientBranch;

    if (!memberClientBranch.clientId) {
      memberClientBranch.clientId = getClientId();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data: any = {
      branchId: branch.id,
      branch: memberClientBranch,
    };

    await updateBranchInformationForGroup(data);
  }

  return;
}

/** Swap to `checkIresProjectNumberPrefixDevelopment` **locally only** to test against the twin; never commit the swap. */
const CHECK_PREFIX_CALLABLE = "checkIresProjectNumberPrefix";

/**
 * Whether another client already holds a Mandantenkürzel, ignoring case (ticket 2610048).
 *
 * Asked of IRES Cloud: this used to be a browser query across all clients, which the rules allow only
 * to IRES clients and admin accounts, so for a customer's master account it failed, the failure was
 * swallowed and every Kürzel counted as free. Creating a member client checks again in IRES Cloud.
 *
 * @param {string} clientId - The client being edited, which may keep its own Kürzel; "" when creating.
 * @param {string} iresProjectNumberPrefix - The Kürzel, four characters.
 * @returns {Promise<boolean>} true when another client holds it. A failed call resolves false, as
 *   before, so a passing network problem never blocks the dialog; creation is checked server-side anyway.
 */
export async function doesOtherClientWithIresProjectNumberPrefixExist(
  clientId: string,
  iresProjectNumberPrefix: string
): Promise<boolean> {
  try {
    const result = await functions.httpsCallable(CHECK_PREFIX_CALLABLE)({ prefix: iresProjectNumberPrefix, clientId });
    return (result.data as { taken?: unknown } | null)?.taken === true;
  } catch {
    // See above: not a refusal, so the dialog goes on; IRES Cloud repeats the check on creation.
    return false;
  }
}

/**
 * Checks if another client exists with the same email address.
 *
 * @param {string} clientId - The ID of the current client.
 * @param {string} email - The email address to check for.
 * @returns {Promise<boolean>} - Resolves to `true` if another client with the same email exists, otherwise `false`.
 * @throws {Error} - Will return `false` in case of any error.
 */
export async function doesOtherClientWithEmailExist(
  clientId: string,
  email: string
) {
  try {
    const clientCollection = firestore().collection('clients');
    const querySnapshot = await clientCollection
      .where('email', '==', email)
      .limit(2)
      .get();

    const matchingClients: MemberClientDetails[] = [];
    querySnapshot.forEach((doc) => {
      matchingClients.push({ id: doc.id, ...doc.data() } as MemberClientDetails);
    });

    return matchingClients.some((client) => client.id !== clientId);
  } catch (error) {
    // [SKIP] Error
    return false;
  }
}

/**
 * Updates the details of a member client.
 *
 * @param {string} memberClientId - The ID of the member client to update.
 * @param {any} update - An object containing the fields to update and their new values.
 * @returns {Promise<boolean>} - Resolves to `true` if the update was successful, otherwise `false`.
 * @throws {Error} - Will return `false` in case of any error.
 */
export async function updateMemberClientDetails(
  memberClientId: string, 
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  update: any
) {
  try {
    await db.doc(getClientRefById(memberClientId)).set(update, { merge: true });
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Calls the `createNewMemberClient` cloud function to create a new member client.
 *
 * @param {MemberClientDetails} memberClientDetails - The details of the member client to be created.
 * @param {string} memberClientPassword - The password for the new member client.
 * @returns {Promise<MemberClientDetails | null>} - Resolves with the member client details if successful, otherwise `null`.
 * @throws {Error} - If the cloud function call fails.
 */
export async function createNewMemberClient(
  memberClientDetails: Partial<MemberClientDetails>,
  memberClientPassword: string
): Promise<MemberClientDetails | null> {
  try {
    const administratorClientId = getClientId();
    const memberClientEmail = memberClientDetails.email;

    const createNewMemberClient = functions.httpsCallable(
      "createNewMemberClient"
    );

    // Prepare the data payload
    const data = {
      administratorClientId,
      memberClientEmail,
      memberClientPassword,
      memberClientDetails,
    };

    // Call the cloud function
    const result = await createNewMemberClient(data);

    if (result.data && result.data.wasSuccessful) {
      // Return the member client details if the creation was successful
      return result.data.client as MemberClientDetails;
    }

    // Return null if the creation was not successful
    return null;
  } catch (error) {
    throw new Error("Failed to create a new member client.");
  }
}